import * as React from 'react';

import useFetchRolesFromIds from 'bundles/onboarding-common/hooks/useFetchRolesFromIds';
import type { SelectedRoleAttribute } from 'bundles/onboarding-common/utils/savedQuestionDataUtils';
import type { SelectedRole } from 'bundles/personalization-settings/utils/desiredRolesV1';

export type UseResolvedDesiredRolesResult = {
  /** The learner's saved desired roles, resolved to display names via the roles catalog. */
  value: SelectedRole[];
  loading: boolean;
};

/**
 * Resolves the page-level query's raw saved desired-roles value (DEFAULT
 * roles carry only an id until resolved against the roles catalog) into
 * display-ready `SelectedRole[]`. Shared by `DesiredRolesSettingItem` (its
 * own editor) and the add-current-role-to-desired-roles prompt
 * (`LearnerObjectSettingItems`), so both read the same resolution instead of
 * duplicating the roles-catalog fetch inline.
 */
const useResolvedDesiredRoles = (savedRoles: SelectedRoleAttribute): UseResolvedDesiredRolesResult => {
  // Only DEFAULT roles missing a name need a catalog round-trip -- the raw
  // page-level query seed carries id-only DEFAULT roles, but every write this
  // hook's callers make already includes the resolved name. Keying the fetch
  // on ids-still-missing-a-name (not all DEFAULT ids) keeps
  // `useFetchRolesFromIds`'s `roleIds` reference stable across saves, so it
  // doesn't re-fetch (and flash its loading state) for roles already known.
  const unresolvedIdsString = (savedRoles ?? [])
    .filter((r) => r.type === 'DEFAULT' && !r.name)
    .map((r) => r.id)
    .join(',');
  const unresolvedIds = React.useMemo(
    () => (unresolvedIdsString ? unresolvedIdsString.split(',') : []),
    [unresolvedIdsString]
  );
  const { roles: resolvedRoles, isLoading: resolveLoading } = useFetchRolesFromIds({ roleIds: unresolvedIds });

  const resolvedNameById = React.useMemo(() => new Map(resolvedRoles.map((r) => [r.id, r.name])), [resolvedRoles]);

  const value = React.useMemo<SelectedRole[]>(() => {
    if (!savedRoles) return [];
    return savedRoles.flatMap((role): SelectedRole[] => {
      if (role.type === 'DEFAULT') {
        const name = role.name || resolvedNameById.get(role.id);
        return name ? [{ type: 'DEFAULT' as const, id: role.id, name }] : [];
      }
      return [{ type: 'CUSTOM' as const, name: role.name }];
    });
  }, [savedRoles, resolvedNameById]);

  return { value, loading: unresolvedIds.length > 0 && resolveLoading };
};

export default useResolvedDesiredRoles;
